import { Fragment } from 'react';
import { useParams } from 'react-router-dom';

import { FormClosed } from '@components/form-closed';
import { Spinner } from '@components/loading-spinner';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages';
import { TooltipStyles } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { isRequestError } from '@utils/requests';

import { JourneyFormIdentifier, useJourneyForm, useSaveJourneyForm } from 'api/form';
import { JourneyFormResponse } from 'api/request';

import { DisplayForm } from './display-form';
import { JourneyFormLoadError } from './journey-form-load-error';
import { SubmissionSuccess } from './submission-success';

type RouteParams = {
  journeyUuid?: string;
  formUuid?: string;
  companySlug?: string;
  formSlug?: string;
};

export const JourneyForm = () => {
  const params = useParams() as RouteParams;
  const identifier: JourneyFormIdentifier =
    params.companySlug && params.formSlug
      ? { kind: 'slug', companySlug: params.companySlug, formSlug: params.formSlug }
      : { kind: 'uuid', journeyUuid: params.journeyUuid!, formUuid: params.formUuid! };
  const formQuery = useJourneyForm(identifier);

  const handleRetry = () => {
    formQuery.refetch();
  };

  if (formQuery.isLoading) {
    return <Spinner />;
  }

  if (formQuery.isError) {
    const status = isRequestError(formQuery.error) ? formQuery.error.response?.status : undefined;
    const isFormUnavailable = status === 404 || status === 403;
    return <JourneyFormLoadError isTemporary={!isFormUnavailable} onRetry={handleRetry} />;
  }

  if (!formQuery.data.hasPublishedJourney) {
    return <JourneyFormLoadError isTemporary={false} onRetry={handleRetry} />;
  }

  return <LoadedJourneyForm form={formQuery.data} />;
};

type FormClosedErrorData = {
  error?: string;
  reason?: JourneyFormResponse['closeReason'];
  closedHeading?: string | null;
  closedMessage?: string | null;
};

const LoadedJourneyForm = ({ form }: { form: JourneyFormResponse }) => {
  const { t } = useScopedTranslate(translations);
  const saveMutation = useSaveJourneyForm(form);
  const showSubmittingSpinner = useRotatingLoadingMessages({
    title: t('justAMoment'),
    isEnabled: saveMutation.isLoading,
    messages: [t('submittingInfo')],
  });

  const submitError = isRequestError<FormClosedErrorData>(saveMutation.error)
    ? saveMutation.error
    : undefined;

  const formClosedError =
    submitError?.response?.status === 400 && submitError.response.data?.error === 'form_closed'
      ? submitError.response.data
      : undefined;
  const isFormClosedError = Boolean(formClosedError);

  const showSubmissionSuccessPage = saveMutation.isSuccess && !showSubmittingSpinner;
  const showDisplayForm =
    !form.isClosed && !isFormClosedError && (!saveMutation.isSuccess || showSubmittingSpinner);

  if (form.isClosed) {
    return (
      <FormClosed
        closedHeading={form.closedHeading}
        closedMessage={form.closedMessage}
        closeReason={form.closeReason}
      />
    );
  }

  if (formClosedError) {
    return (
      <FormClosed
        closedHeading={formClosedError.closedHeading}
        closedMessage={formClosedError.closedMessage}
        closeReason={formClosedError.reason ?? null}
      />
    );
  }

  return (
    <Fragment>
      <TooltipStyles />
      {showSubmissionSuccessPage && (
        <SubmissionSuccess submissionMessageConfig={form.submissionMessageConfig} />
      )}

      {showDisplayForm && (
        <DisplayForm
          formConfig={form.fieldsConfig}
          lists={form.lists}
          onSubmit={saveMutation.mutateAsync}
          hideSubmitSpinner
        />
      )}
    </Fragment>
  );
};

const translations = {
  en: {
    justAMoment: 'Just a moment',
    submittingInfo: 'Submitting information...',
  },
  is: {
    justAMoment: 'Augnablik',
    submittingInfo: 'Sendi upplýsingar...',
  },
};
